import { useState } from 'react';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useMediaQuery } from 'react-responsive';

import useAccountQuery from 'api/queries/useAccountQuery';
import OrderItem from 'features/account/orders/OrderItem';
import { Alert } from 'honeydew/Alert';
import Button from 'honeydew/Button';
import { Divider } from 'honeydew/Divider';
import Stepper from 'honeydew/Stepper';
import useTailwindTheme from 'hooks/useTailwindTheme';
import { BuyItAgainCTA } from 'shared/BuyItAgainCTA';
import DriverTipsAlert from 'shared/driver-tips/DriverTipsAlert';
import { trackEvent } from 'utils/analyticsUtils';
import { formatDateString } from 'utils/dateUtils';
import { handleSetShippingStatus } from 'utils/globalUtils';
import {
    displayReportIssue,
    getRefundAmount,
    hasAnyIssueReported,
    hasBeenDeliveredForOneDay,
    hasItemIssueReported,
    hasOtherIssueReported,
    hasShipmentIssueReported,
    itemHasIssueReported,
} from 'utils/orderUtils';

import CancelOrderInfoModal from '../CancelOrderInfoModal';
import NeedHelpBanner from '../NeedHelpBanner';

function OrderShipment({ boxNumber, cancelledOrder, isImperfectLegacy, multiShip, openProductModal, order, shipment }) {
    const { data: account = {} } = useAccountQuery();
    const { featureCanLateCancel } = account.featureFlags ?? {};

    const tailwindTheme = useTailwindTheme();
    const mobile = useMediaQuery({ maxWidth: tailwindTheme?.screens?.md });

    const [cancelOrderInfoModal, setCancelOrderInfoModal] = useState(false);

    const { customerDrinksOrderFeedback = {}, customerOrderFeedback = {} } = shipment;
    const { chargeID, subtotal, tipAmount, total } = order.charge ?? {};
    const { subtotal: drinksSubtotal, total: drinksTotal } = order.drinksCharge ?? {};
    const { tipAmountRefunded } = order.refundHistory;

    const orderSubtotal = subtotal + (order.hasDrinks ? drinksSubtotal : 0);
    const orderTotal = total + (order.hasDrinks ? drinksTotal : 0);

    const { shippingStatus } = handleSetShippingStatus(shipment.shippingStatus);
    const deliveredForOneDay = hasBeenDeliveredForOneDay(shipment.shippingStatus, shipment.deliverDate);
    const orderScheduled = shippingStatus === 'Scheduled';
    const orderShipped = shippingStatus === 'Shipped';
    const orderDelivered = shippingStatus === 'Delivered';
    const orderConfirmed = shippingStatus === 'Confirmed';
    const orderProcessing = shippingStatus === 'Processing';
    const orderCancelled = shippingStatus === 'Canceled';
    const orderDetailsSteps = [
        {
            id: 1,
            name: 'Confirmed',
            status: !orderScheduled ? 'done' : 'upcoming',
            subtext: shipment?.createdAt?.date
                ? `${orderScheduled ? 'Est ' : ''}${formatDateString(shipment.createdAt.date)}`
                : '',
        },
        {
            id: 2,
            name: 'Processing',
            status: orderProcessing || orderShipped || orderDelivered ? 'done' : 'upcoming',
            subtext: '',
        },
        {
            id: 3,
            name: 'Shipped',
            status: orderShipped || orderDelivered ? 'done' : 'upcoming',
            subtext: '',
        },
        {
            id: 4,
            name: 'Delivered',
            status: orderDelivered ? 'done' : (orderShipped && 'in progress') || 'upcoming',
            subtext: shipment?.deliverDate
                ? `${!orderDelivered ? 'Est ' : ''}${formatDateString(shipment.deliverDate)}`
                : '',
        },
    ];

    const anyIssueReported = hasAnyIssueReported(customerOrderFeedback, customerDrinksOrderFeedback);
    const itemIssueReported = hasItemIssueReported(customerOrderFeedback, customerDrinksOrderFeedback);
    const shipmentIssueReported = hasShipmentIssueReported(customerOrderFeedback, customerDrinksOrderFeedback);
    const otherIssueReported = hasOtherIssueReported(customerOrderFeedback, customerDrinksOrderFeedback);

    const isLateCancelEligible = (orderConfirmed || orderScheduled) && !cancelledOrder && featureCanLateCancel;
    const isOrderFeedbackEligible =
        shipment?.createdAt?.date &&
        displayReportIssue(shipment.createdAt.date) &&
        shipment.items.length > 0 &&
        !orderScheduled &&
        !orderConfirmed &&
        !orderCancelled &&
        !isImperfectLegacy;

    const openCancelOrderInfoModal = () => {
        setCancelOrderInfoModal(true);
        trackEvent('late cancel ineligible info click');
    };

    return (
        <div key={shipment.shipmentID} className="mt-30">
            <div
                className={clsx('mb-20', {
                    'items-center justify-between md:grid md:grid-cols-[1fr_200px]': isImperfectLegacy,
                })}
            >
                <div className="flex-row-reverse items-center justify-end gap-10 md:flex">
                    <span className="mt-5 md:mt-0">
                        <h3 className="text-body-lg font-grotesk-bold">{shippingStatus}</h3>
                        {multiShip && <p className="text-body-md font-grotesk-bold">Box {boxNumber}</p>}
                    </span>
                </div>

                {isImperfectLegacy && (
                    <div className="mt-20 md:mt-0 md:w-[200px]">
                        <Button
                            as="link"
                            id="orderDetailsTrack"
                            size="md"
                            to={`/account/orders/details/${order.id}/${shipment.shipmentNumber}/track`}
                            onClick={() => trackEvent('order track click')}
                        >
                            Track My Order
                        </Button>
                    </div>
                )}
            </div>

            {!cancelledOrder && (
                <>
                    {!isImperfectLegacy && (
                        <div className="flex flex-col items-stretch gap-20 lg:grid lg:grid-cols-[1fr_200px] lg:gap-50">
                            <div className="text-body-sm">
                                <Stepper
                                    orientation={mobile ? 'vertical' : 'horizontal'}
                                    steps={orderDetailsSteps}
                                    containerWidth={mobile ? '260px' : '100%'}
                                />
                            </div>

                            {!orderScheduled && (
                                <div className="text-body-sm">
                                    {shipment.hasDrinks ? (
                                        <Button
                                            {...(!window.isNativeApp && { as: 'a' })}
                                            to={shipment.trackingUrl}
                                            id="wineOrderDetailsTrack"
                                            size="md"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => {
                                                if (window.isNativeApp) {
                                                    window.ReactNativeWebView?.postMessage(
                                                        JSON.stringify({
                                                            navigate: 'Tracking',
                                                            url: shipment.trackingUrl,
                                                        })
                                                    );
                                                }
                                            }}
                                        >
                                            Track
                                        </Button>
                                    ) : (
                                        <Button
                                            as="link"
                                            id="orderDetailsTrack"
                                            size="md"
                                            to={`/account/orders/details/${order.id}/${shipment.shipmentNumber}/track`}
                                            onClick={() => trackEvent('order track click')}
                                        >
                                            Track My Order
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {orderProcessing && (
                        <button
                            type="button"
                            className="text-body-sm mt-10 flex items-center"
                            onClick={openCancelOrderInfoModal}
                        >
                            Order not eligible for cancellation{' '}
                            <InformationCircleIcon width={18} className="ml-10 text-black" />
                        </button>
                    )}
                </>
            )}

            <NeedHelpBanner
                displayReportIssue={isOrderFeedbackEligible}
                displayCancelOrder={isLateCancelEligible}
                orderID={order.id}
                chargeID={chargeID}
                boxNumber={boxNumber}
            />

            {anyIssueReported && (
                <div className="mt-30">
                    <Alert variant="success">
                        {itemIssueReported && <span className="block font-grotesk-bold">Item Issue Submitted</span>}
                        {shipmentIssueReported && (
                            <span className="block font-grotesk-bold">Delivery Issue Submitted #{order.id}</span>
                        )}
                        {otherIssueReported && (
                            <span className="block font-grotesk-bold">&quot;Other&quot; Issue Submitted</span>
                        )}
                        <span>Our Customer Care team will help resolve your issue(s) via email.</span>
                    </Alert>
                </div>
            )}

            {!deliveredForOneDay && boxNumber === 1 && (
                <DriverTipsAlert
                    tipAmount={tipAmount}
                    chargeID={chargeID}
                    tipAmountRefunded={tipAmountRefunded}
                    orderTotal={orderTotal}
                    orderSubtotal={orderSubtotal}
                />
            )}

            <div className="mt-30">
                {!cancelledOrder ? (
                    <>
                        {shipment.hasRefundsApplied && <Alert variant="error">Refunds have been applied</Alert>}
                        {shipment.hasOOSItems && (
                            <Alert variant="error">You were not charged for any out of stock item(s)</Alert>
                        )}
                    </>
                ) : (
                    <Alert variant="error">You will not be charged.</Alert>
                )}

                <Divider />

                {order.productID === 6 ? (
                    shipment.items.length > 0 && (
                        <div>
                            {account.marketplaceOpen && !window.isNativeApp && (
                                <>
                                    <BuyItAgainCTA />
                                    <Divider />
                                </>
                            )}
                            {shipment.items.map((item) => (
                                <div
                                    key={item.id}
                                    role="button"
                                    tabIndex="0"
                                    onClick={() => openProductModal(item)}
                                    onKeyDown={() => openProductModal(item)}
                                    className="w-full"
                                >
                                    <OrderItem
                                        item={item}
                                        cancelled={orderCancelled}
                                        totalLoyaltyPointsRefunded={0}
                                        refundedCredits={getRefundAmount(item, customerOrderFeedback)}
                                        issueReported={itemHasIssueReported(
                                            item,
                                            customerOrderFeedback,
                                            customerDrinksOrderFeedback
                                        )}
                                        isImperfectLegacy={isImperfectLegacy}
                                    />
                                    <Divider />
                                </div>
                            ))}
                            {shipment.hasDrinks && (
                                <div className="text-body-xs my-20">
                                    <p>
                                        You must be 21+ to purchase this item. Valid ID required at delivery. Wine is
                                        sold and ships separately and does not apply to your grocery order.
                                    </p>
                                    <p>
                                        All wine orders are reviewed, accepted, and fulfilled by Hemispheres, LLC, 8418
                                        S Lac Jac, Parlier, CA 93648.
                                    </p>
                                </div>
                            )}
                        </div>
                    )
                ) : (
                    <div>
                        <p className="text-body-sm font-grotesk-bold">
                            Your {order.productID === 5 ? 'Madness' : 'Mischief'} box
                        </p>

                        {shipment.items?.length > 0 ? (
                            shipment.items.map((item) => (
                                <div
                                    key={item.id}
                                    role="button"
                                    tabIndex="0"
                                    onClick={() => openProductModal(item)}
                                    onKeyDown={() => openProductModal(item)}
                                    className="w-full"
                                >
                                    <OrderItem item={item.model} isImperfectLegacy={isImperfectLegacy} baseBox />
                                </div>
                            ))
                        ) : (
                            <p className="text-body-sm">
                                A mixed variety of {order.productID === 5 ? '14' : '12'} types of produce.
                            </p>
                        )}

                        {order?.addons?.length > 0 && (
                            <>
                                <p className="mt-[55px] font-grotesk-bold">Marketplace picks</p>

                                {order.addons.map((item) => (
                                    <div
                                        key={item.id}
                                        role="button"
                                        tabIndex="0"
                                        onClick={() => openProductModal(item)}
                                        onKeyDown={() => openProductModal(item)}
                                        className="w-full"
                                    >
                                        <OrderItem item={item.model} isImperfectLegacy={isImperfectLegacy} />
                                    </div>
                                ))}
                            </>
                        )}
                    </div>
                )}
            </div>

            <CancelOrderInfoModal
                cancelOrderInfoModal={cancelOrderInfoModal}
                setCancelOrderInfoModal={setCancelOrderInfoModal}
            />
        </div>
    );
}

OrderShipment.propTypes = {
    boxNumber: PropTypes.number.isRequired,
    cancelledOrder: PropTypes.bool.isRequired,
    isImperfectLegacy: PropTypes.bool.isRequired,
    multiShip: PropTypes.bool.isRequired,
    openProductModal: PropTypes.func.isRequired,
    order: PropTypes.shape({}).isRequired,
    shipment: PropTypes.shape({}).isRequired,
};

export default OrderShipment;
